import React, { useEffect } from 'react';
import { Box, Typography, Chip, Button } from '@mui/material';
import { CustomAvatar, useAvatarUrl } from '@europrocurement/flexy-components';
import { User } from '../../../types/type';

export type HeaderUser = Pick<
    User,
    'prenom' | 'nom' | 'id' | 'isDeleted' | 'initiales' | 'mail' | 'userIdentifier'
>;

export type UserHeaderProps = {
    user: HeaderUser;
    loggedUser: string | undefined;
    synchroniseWithKCAccount: (id: number) => void;
};

export const UserHeader: React.FunctionComponent<UserHeaderProps> = function ({
    user,
    loggedUser,
    synchroniseWithKCAccount,
}) {
    const [avatarSrc, setAvatarSrc] = React.useState<string | undefined>(undefined);
    const { id } = user;
    const { avatarUrl, usersAvatarInfos } = useAvatarUrl();

    if (!id) {
        throw new Error("L'identifiant de l'utilisateur est manquant.");
    }

    useEffect(() => {
        if (user.userIdentifier !== undefined) {
            if (user.userIdentifier === loggedUser) {
                setAvatarSrc(avatarUrl);
            } else {
                const result = usersAvatarInfos?.find(
                    (element) => element.avatarUserIdentifier === user.userIdentifier,
                )?.currentUserAvatarUrl;

                if (result !== undefined) {
                    setAvatarSrc(result);
                }
            }
        }
    }, [avatarUrl, loggedUser, user.userIdentifier, usersAvatarInfos]);

    return (
        <Box
            display="flex"
            justifyContent="space-between"
            sx={{ margin: '0px 0px 18px 0px' }}
        >
            <Box display="flex">
                <CustomAvatar
                    firstName={user.prenom}
                    lastName={user.nom || 'Inconnu'}
                    src={avatarSrc}
                    sx={{
                        width: 64,
                        height: 64,
                    }}
                />
                <Box
                    display="flex"
                    flexDirection="column"
                    sx={{ marginLeft: '15px' }}
                >
                    <Typography
                        variant="l2dh5"
                        sx={{ lineHeight: '1.4' }}
                    >
                        {`${user.prenom} ${user.nom}`}
                        <Typography
                            data-testid="initiales"
                            variant="l2dh7"
                            component="span"
                            color="text.secondary"
                            sx={{ lineHeight: '1.4' }}
                        >
                            {user.initiales && `  (${user.initiales})`}
                        </Typography>
                        {user.isDeleted ? (
                            <Chip
                                sx={{ marginLeft: '8px' }}
                                color="default"
                                label="Compte désactivé"
                            />
                        ) : (
                            <Chip
                                sx={{ marginLeft: '8px' }}
                                color="primary"
                                label="Compte actif"
                            />
                        )}
                    </Typography>
                    <Typography
                        data-testid="idUser"
                        sx={{ lineHeight: '2' }}
                        variant="l2dbodys"
                        color="text.gris70"
                    >
                        <b>Identifiant : </b>
                        {user.id ? `${user.id}` : 'non défini'}
                    </Typography>
                </Box>
            </Box>

            {/* Faire appel à l'API pour récupérer les données */}
            <Button onClick={() => synchroniseWithKCAccount(id)}>Synchroniser avec Keycloak</Button>
        </Box>
    );
};
